import React, { useState, useMemo } from 'react';
import { 
  Calculator, 
  DollarSign, 
  Percent, 
  TrendingUp, 
  BarChart3, 
  Target, 
  Settings, 
  ArrowLeft,
  Activity,
  Plus,
  Edit,
  Eye
} from 'lucide-react';
import { pricingTools } from '../../data/salesCycleData';

const PricingTools = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pricingModel, setPricingModel] = useState('cost_plus');
  const [customCost, setCustomCost] = useState('');
  const [customMargin, setCustomMargin] = useState('');
  const [quantity, setQuantity] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const calculatePrice = (cost, model, margin = 20, quantity = 1) => {
    switch (model) {
      case 'cost_plus':
        return cost * (1 + margin / 100);
      case 'value_based':
        return cost * 1.5; // 假設價值基礎定價
      case 'competitive':
        return cost * 1.2; // 假設競爭定價
      default:
        return cost;
    }
  };

  const getVolumeDiscount = (quantity) => {
    const tiers = pricingTools.discount_policies[0].tiers;
    for (let tier of tiers) {
      if (quantity >= tier.min_quantity && (tier.max_quantity === null || quantity <= tier.max_quantity)) {
        return tier.discount_rate;
      }
    }
    return 0;
  };

  const renderWorkflowBanner = () => {
    if (navigationSource === 'sales-workflow') {
      return (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-4 mb-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-green-500 p-2 rounded-full">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-green-800">來自業務流程: 銷售週期管理</h3>
                <p className="text-sm text-green-600">階段B: 產品報價與解決方案提案 - 定價工具</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>返回銷售週期</span>
            </button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">定價工具儀表板</h2>
        <button
          onClick={() => setActiveView('calculator')}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Calculator className="w-4 h-4" />
          <span>定價計算器</span>
        </button>
      </div>

      {/* 成本結構概覽 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">成本結構分析</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="bg-blue-100 p-4 rounded-lg mb-2">
              <p className="text-2xl font-bold text-blue-600">
                {pricingTools.cost_structure.material_cost_percentage}%
              </p>
            </div>
            <p className="text-sm font-medium text-gray-600">材料成本</p>
          </div>
          <div className="text-center">
            <div className="bg-green-100 p-4 rounded-lg mb-2">
              <p className="text-2xl font-bold text-green-600">
                {pricingTools.cost_structure.labor_cost_percentage}%
              </p>
            </div>
            <p className="text-sm font-medium text-gray-600">人工成本</p>
          </div>
          <div className="text-center">
            <div className="bg-yellow-100 p-4 rounded-lg mb-2">
              <p className="text-2xl font-bold text-yellow-600">
                {pricingTools.cost_structure.overhead_cost_percentage}%
              </p>
            </div>
            <p className="text-sm font-medium text-gray-600">間接成本</p>
          </div>
          <div className="text-center">
            <div className="bg-purple-100 p-4 rounded-lg mb-2">
              <p className="text-2xl font-bold text-purple-600">
                {pricingTools.cost_structure.profit_margin_percentage}%
              </p>
            </div>
            <p className="text-sm font-medium text-gray-600">利潤率</p>
          </div>
        </div>
      </div>

      {/* 定價模式 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">定價模式</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {pricingTools.pricing_models.map((model, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <h4 className="font-medium text-gray-800 mb-2">{model.model_name}</h4>
              <p className="text-sm text-gray-600 mb-2">{model.description}</p>
              <p className="text-sm font-mono bg-gray-100 p-2 rounded">{model.formula}</p>
              <p className="text-xs text-blue-600 mt-2">適用: {model.use_case}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 折扣政策 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">折扣政策</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {pricingTools.discount_policies.map((policy, index) => (
            <div key={index}>
              <h4 className="font-medium text-gray-800 mb-3">{policy.name}</h4>
              {policy.tiers && (
                <div className="space-y-2">
                  {policy.tiers.map((tier, tierIndex) => (
                    <div key={tierIndex} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm text-gray-600">
                        {tier.min_quantity}+ 
                        {tier.max_quantity ? ` - ${tier.max_quantity}` : '+'}
                      </span>
                      <span className="text-sm font-medium text-green-600">{tier.discount_rate}%</span>
                    </div>
                  ))}
                </div>
              )}
              {policy.options && (
                <div className="space-y-2">
                  {policy.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm text-gray-600">{option.terms}</span>
                      <span className={`text-sm font-medium ${
                        option.discount_rate >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {option.discount_rate >= 0 ? '+' : ''}{option.discount_rate}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {policy.periods && (
                <div className="space-y-2">
                  {policy.periods.map((period, periodIndex) => (
                    <div key={periodIndex} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm text-gray-600">{period.period}</span>
                      <span className="text-sm font-medium text-green-600">{period.discount_rate}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 產品價格表 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">產品價格表</h3>
          <button
            onClick={() => setActiveView('price_list')}
            className="text-blue-600 hover:text-blue-800 font-medium"
          >
            查看完整價格表
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  產品名稱
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  標準價格
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  成本
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  毛利率
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  競爭對手價格
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pricingTools.price_lists.map((product, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{product.product_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(product.standard_price)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatCurrency(product.cost)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      product.margin_percentage > 20 ? 'bg-green-100 text-green-800' :
                      product.margin_percentage > 15 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {product.margin_percentage.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {product.competitor_prices.map((comp, i) => (
                        <div key={i} className="flex justify-between">
                          <span>{comp.competitor}:</span>
                          <span>{formatCurrency(comp.price)}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedProduct(product);
                        setActiveView('product_detail');
                      }}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="text-green-600 hover:text-green-900">
                      <Edit className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderCalculator = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">定價計算器</h2>
        <button
          onClick={() => setActiveView('dashboard')}
          className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>返回儀表板</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 輸入參數 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">輸入參數</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">產品成本</label>
              <input
                type="number"
                value={customCost}
                onChange={(e) => setCustomCost(e.target.value)}
                placeholder="輸入產品成本"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">利潤率 (%)</label>
              <input
                type="number"
                value={customMargin}
                onChange={(e) => setCustomMargin(e.target.value)}
                placeholder="輸入利潤率"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">數量</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="輸入數量"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">定價模式</label>
              <select
                value={pricingModel}
                onChange={(e) => setPricingModel(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cost_plus">成本加成定價</option>
                <option value="value_based">價值導向定價</option>
                <option value="competitive">競爭導向定價</option>
              </select>
            </div>
          </div>
        </div>

        {/* 計算結果 */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">計算結果</h3>
          {customCost && customMargin && quantity ? (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">基礎價格</p>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency(calculatePrice(parseFloat(customCost), pricingModel, parseFloat(customMargin)))}
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">數量折扣</p>
                <p className="text-2xl font-bold text-green-600">
                  {getVolumeDiscount(parseInt(quantity))}%
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">最終價格</p>
                <p className="text-2xl font-bold text-purple-600">
                  {formatCurrency(
                    calculatePrice(parseFloat(customCost), pricingModel, parseFloat(customMargin)) * 
                    (1 - getVolumeDiscount(parseInt(quantity)) / 100)
                  )}
                </p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600">總金額</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {formatCurrency(
                    calculatePrice(parseFloat(customCost), pricingModel, parseFloat(customMargin)) * 
                    (1 - getVolumeDiscount(parseInt(quantity)) / 100) * 
                    parseInt(quantity)
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">請輸入所有參數以開始計算</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderPriceList = () => (
    <div className="space-y-6">
      {renderWorkflowBanner()}
      
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-800">完整價格表</h2>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveView('create')}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>新增產品</span>
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回儀表板</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                產品名稱
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                標準價格
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                成本
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                毛利
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                毛利率
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                競爭對手價格
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                操作
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pricingTools.price_lists.map((product, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{product.product_name}</div>
                  <div className="text-sm text-gray-500">{product.product_id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-bold text-gray-900">{formatCurrency(product.standard_price)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatCurrency(product.cost)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatCurrency(product.margin)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    product.margin_percentage > 20 ? 'bg-green-100 text-green-800' :
                    product.margin_percentage > 15 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {product.margin_percentage.toFixed(1)}%
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 space-y-1">
                    {product.competitor_prices.map((comp, i) => (
                      <div key={i} className="flex justify-between">
                        <span className="text-gray-600">{comp.competitor}:</span>
                        <span className={`font-medium ${
                          comp.price > product.standard_price ? 'text-green-600' :
                          comp.price < product.standard_price ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          {formatCurrency(comp.price)}
                        </span>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedProduct(product);
                      setActiveView('product_detail');
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button className="text-green-600 hover:text-green-900">
                    <Edit className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'calculator':
        return renderCalculator();
      case 'price_list':
        return renderPriceList();
      case 'create':
        return (
          <div className="space-y-6">
            {renderWorkflowBanner()}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">新增產品價格</h2>
              <p className="text-gray-600">產品價格新增功能開發中...</p>
              <button
                onClick={() => setActiveView('price_list')}
                className="mt-4 flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>返回價格表</span>
              </button>
            </div>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default PricingTools; 